/**
 * Whether moving the planting date would disturb the environment the user configured.
 *
 * Mirrors the defaulting cascade in `useRecommendationEditData` (`recommendation-aio/hooks.ts`):
 * each `next*` helper returns what that cascade would write for the new date's options, so a
 * value that comes back different is one the environment step would reset. Keep them in step
 * with that hook.
 */

type Choice = { value: number | null } | null;

/** The environment value the cascade writes when a location has no moisture or soil data. */
export const FALLBACK_ENVIRONMENT = 9000;

export const nextSoilMoisture = (current: number | null | undefined, choices: Choice[]) => {
    if (!choices.length) return null;
    if (choices.some((choice) => choice?.value === current)) return current;
    return choices[Math.min(Math.floor(choices.length / 2), choices.length - 1)]?.value ?? null;
};

/** The cascade treats a falsy current value as unset, so it is re-defaulted even when listed. */
const nextChoice = (current: number | null | undefined, choices: Choice[]) => {
    if (current && choices.some((choice) => choice?.value === current)) return current;
    if (choices.some((choice) => choice === null)) return null;
    return choices.length ? (choices[0]?.value ?? null) : null;
};

export const nextSoilType = nextChoice;
export const nextIrrigation = nextChoice;

export type PlantingDateConfig = {
    soilMoisture?: number | null;
    soilType?: number | null;
    irrigation?: number | null;
    environment?: number;
    recommendedEnvironment?: unknown;
    products?: number[];
};

/**
 * `true` when the new date keeps the environment and every chosen hybrid. Each option list is
 * `undefined` when the cascade wouldn't query it for this config either, and is then skipped.
 * The providers swallow errors into empty lists or a fallback environment, which read as a
 * change here — a failed check never skips the warning.
 */
export const keepsEnvironment = ({
    config,
    soilMoistures,
    soilTypes,
    environments,
    offeredHybridIds,
}: {
    config: PlantingDateConfig;
    soilMoistures?: Choice[];
    soilTypes?: { soilTypesChoices: Choice[]; irrigationChoices: Choice[] };
    environments?: unknown;
    offeredHybridIds: number[];
}) => {
    const fallsBackToDefault = config.environment === FALLBACK_ENVIRONMENT;

    if (soilMoistures) {
        if (!soilMoistures.length && !(config.soilMoisture == null && fallsBackToDefault)) return false;
        if (nextSoilMoisture(config.soilMoisture, soilMoistures) !== config.soilMoisture) return false;
    }

    if (soilTypes) {
        const { soilTypesChoices, irrigationChoices } = soilTypes;
        if (!soilTypesChoices.length && !irrigationChoices.length && !fallsBackToDefault) return false;
        if (nextSoilType(config.soilType, soilTypesChoices) !== config.soilType) return false;
        if (nextIrrigation(config.irrigation, irrigationChoices) !== config.irrigation) return false;
    }

    // Same test the cascade uses to decide whether to re-default the environment.
    if (
        environments !== undefined &&
        !(config.environment && JSON.stringify(config.recommendedEnvironment) === JSON.stringify(environments))
    ) {
        return false;
    }

    return (config.products || []).every((id) => offeredHybridIds.includes(id));
};
